import React, { useState, useEffect } from 'react'
import {
  Card,
  Table,
  Input,
  Button,
  Space,
  message,
  Tag,
  Typography,
  Checkbox,
  Spin,
  Popconfirm
} from 'antd'
import {
  SearchOutlined,
  CheckOutlined,
  CloseOutlined,
  ReloadOutlined
} from '@ant-design/icons'
import api from '../../utils/api'
import styled from 'styled-components'
import AdminTable from '../../components/common/AdminTable'
import { formatTz } from '../../utils/timezone'

const { Title } = Typography
const { Search } = Input

// 莫兰迪色系配色
const colors = {
  cardBackground: '#faf9f6',
  border: 'rgba(168, 181, 192, 0.3)',
  shadow: 'rgba(154, 163, 175, 0.2)',
  textPrimary: '#5a5a5a',
  textSecondary: '#8a8a8a'
}

const StyledCard = styled(Card)`
  background: ${colors.cardBackground};
  border-radius: 16px;
  box-shadow: 0 4px 16px ${colors.shadow};
  border: 1px solid ${colors.border};
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  .ant-card-body {
    padding: 24px;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;

    @media (max-width: 768px) {
      padding: 16px 12px;
    }
  }

  @media (max-width: 768px) {
    border-radius: 12px;
    margin: 0;
  }
`

const SearchContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`

const ActionButtons = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;

    button {
      width: 100%;
    }
  }
`

const ArticlePermissionManagement = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  })
  // 获取用户列表
  const fetchUsers = async (page = 1, pageSize = 10, search = '') => {
    setLoading(true)
    try {
      // 构建查询参数
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      })

      // 添加搜索参数（如果存在）
      if (search && search.trim()) {
        params.append('search', search.trim())
      }

      const response = await api.get(`/api/admin/users?${params.toString()}`)

      // 兼容不同的响应格式
      const responseData = response.data
      if (responseData.success || responseData.data) {
        setData(responseData.data || [])
        setPagination({
          current: responseData.pagination?.currentPage || page,
          pageSize: responseData.pagination?.itemsPerPage || pageSize,
          total: responseData.pagination?.totalItems || 0
        })
      } else {
        message.error('獲取用戶列表失敗')
      }
    } catch (error) {
      console.error('獲取用戶列表錯誤:', error)
      message.error('獲取用戶列表失敗')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize, searchTerm)
  }, [])

  // 处理搜索
  const handleSearch = (value) => {
    setSearchTerm(value)
    setSelectedRowKeys([]) // 清空选择
    fetchUsers(1, pagination.pageSize, value)
  }

  // 处理表格变化（分页、排序等）
  const handleTableChange = (newPagination) => {
    setPagination(newPagination)
    fetchUsers(newPagination.current, newPagination.pageSize, searchTerm)
  }

  // 批量设置文章权限
  const handleBatchApprove = async (approve = true) => {
    if (selectedRowKeys.length === 0) {
      message.warning('請先選擇要操作的用戶')
      return
    }

    setLoading(true)
    try {
      const promises = selectedRowKeys.map((userId) =>
        api.put(`/api/admin/users/${userId}/article-permission`, {
          isApprovedForArticle: approve,
          approvalNote: approve
            ? '管理員批量授予發文權限'
            : '管理員批量撤銷發文權限'
        })
      )

      await Promise.all(promises)
      message.success(
        `已${approve ? '授予' : '撤銷'} ${
          selectedRowKeys.length
        } 位用戶的發文權限`
      )
      setSelectedRowKeys([])
      fetchUsers(pagination.current, pagination.pageSize, searchTerm)
    } catch (error) {
      console.error('批量設置權限錯誤:', error)
      message.error('批量設置權限失敗')
    } finally {
      setLoading(false)
    }
  }

  // 单个用户设置文章权限
  const handleTogglePermission = async (userId, currentStatus) => {
    setLoading(true)
    try {
      const newStatus = !currentStatus
      const response = await api.put(
        `/api/admin/users/${userId}/article-permission`,
        {
          isApprovedForArticle: newStatus,
          approvalNote: newStatus ? '管理員授予發文權限' : '管理員撤銷發文權限'
        }
      )

      if (response.data.success) {
        message.success(`已${newStatus ? '授予' : '撤銷'}該用戶的發文權限`)
        fetchUsers(pagination.current, pagination.pageSize, searchTerm)
      } else {
        message.error('設置權限失敗')
      }
    } catch (error) {
      console.error('設置權限錯誤:', error)
      message.error('設置權限失敗')
    } finally {
      setLoading(false)
    }
  }

  // 表格列定义
  const columns = [
    {
      title: '用戶名稱',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => text || record.username || '-'
    },
    {
      title: '用戶名',
      dataIndex: 'username',
      key: 'username'
    },
    {
      title: '電子郵件',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: '電話',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => text || '-'
    },
    {
      title: '發文權限',
      dataIndex: 'isApprovedForArticle',
      key: 'isApprovedForArticle',
      render: (isApproved, record) => (
        <Space>
          <Tag color={isApproved ? 'green' : 'default'}>
            {isApproved ? '已授權' : '未授權'}
          </Tag>
          {isApproved && record.articleApprovalDate && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              {formatTz(record.articleApprovalDate, 'YYYY/MM/DD')}
            </span>
          )}
        </Space>
      )
    },
    {
      title: '註冊時間',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) =>
        formatTz(date, 'YYYY/MM/DD')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          {record.isApprovedForArticle ? (
            <Popconfirm
              title='確定要撤銷該用戶的發文權限嗎？'
              onConfirm={() => handleTogglePermission(record._id, true)}
              okText='確定'
              cancelText='取消'
            >
              <Button type='link' danger size='small' icon={<CloseOutlined />}>
                撤銷權限
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title='確定要授予該用戶發文權限嗎？'
              onConfirm={() => handleTogglePermission(record._id, false)}
              okText='確定'
              cancelText='取消'
            >
              <Button type='link' size='small' icon={<CheckOutlined />}>
                授予權限
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  // 行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys)
    },
    getCheckboxProps: (record) => ({
      disabled: false
    })
  }

  return (
    <StyledCard>
      <Title level={2}>文章發文權限管理</Title>

      {/* 搜索框 */}
      <SearchContainer>
        <Search
          placeholder='搜索用戶名稱、用戶名、電子郵件或電話'
          allowClear
          enterButton={<SearchOutlined />}
          size='large'
          style={{
            maxWidth: '100%',
            width: '100%',
            flex: 1,
            minWidth: 0
          }}
          onSearch={handleSearch}
          onChange={(e) => {
            // 如果清空搜索框，立即搜索
            if (!e.target.value) {
              handleSearch('')
            }
          }}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            setSearchTerm('')
            setSelectedRowKeys([])
            fetchUsers(1, pagination.pageSize, '')
          }}
        >
          刷新
        </Button>
      </SearchContainer>

      {/* 批量操作按钮 */}
      {selectedRowKeys.length > 0 && (
        <ActionButtons>
          <Popconfirm
            title={`確定要授予選中的 ${selectedRowKeys.length} 位用戶發文權限嗎？`}
            onConfirm={() => handleBatchApprove(true)}
            okText='確定'
            cancelText='取消'
          >
            <Button type='primary' icon={<CheckOutlined />} disabled={loading}>
              批量授予權限 ({selectedRowKeys.length})
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`確定要撤銷選中的 ${selectedRowKeys.length} 位用戶發文權限嗎？`}
            onConfirm={() => handleBatchApprove(false)}
            okText='確定'
            cancelText='取消'
          >
            <Button danger icon={<CloseOutlined />} disabled={loading}>
              批量撤銷權限 ({selectedRowKeys.length})
            </Button>
          </Popconfirm>
          <Button onClick={() => setSelectedRowKeys([])} disabled={loading}>
            取消選擇
          </Button>
        </ActionButtons>
      )}

      {/* 用户列表表格 */}
      <AdminTable
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        rowKey='_id'
        loading={loading}
        pagination={{
          currentPage: pagination.current,
          totalItems: pagination.total,
          itemsPerPage: pagination.pageSize
        }}
        onChange={handleTableChange}
      />
    </StyledCard>
  )
}

export default ArticlePermissionManagement
